/// <reference types="electron-api" />

import type { UniqueType } from '../utils/ts-utils';
import type { ScreenInfo } from './db/user.interface';
import type { Point, Rectangle, Size } from './geometry.interface';
import type { LocalIagoEvent } from './iago-event.interface';
import type { MediaPlayerType } from './robot-special-keys.interface';

export const UNSUPPORTED_IPC_MESSAGE = 'unsupported-ipc-message';
export type WindowId = UniqueType<string, 'WindowId'>;
export type ForceIceMode =
  | 'localhost'
  | 'host'
  | 'host-ipv4'
  | 'host-ipv6'
  | 'stun'
  | 'turn'
  | 'turn-udp'
  | 'turn-tcp';
export type AppLaunchInfo = {
  didAppLaunchAfterAutoUpdate: boolean;
  didAppLaunchAfterUserUpdate: boolean;
  didAppLaunchAfterReboot: boolean;
  /**
   * Whether this run has a tray/status icon for Pop. Normally true on mac/windows; on Linux probed at
   * launch via the StatusNotifierWatcher DBus service (a capability, not a distro check — stock GNOME
   * is false, GNOME+AppIndicator is true). `--no-tray` forces false on every platform. With no tray, the
   * dashboard stays reachable through in-app affordances (HUD Pop button, full-dashboard settings entry,
   * quit alert on dashboard close).
   */
  hasTray: boolean;
  automation: {
    /** Dev-user numbers to invite, e.g. [2, 3] → dev-user-002, dev-user-003. */
    inviteUsers?: number[];
    /** Restrict P2P ICE to loopback candidates only. Useful for local dummynet automation. */
    loopbackNetwork: boolean;
    /** Restrict P2P ICE candidates to a specific automation path. */
    forceIce?: ForceIceMode;
    /** Comma-separated automation roles: 'invite,screen-share' or 'auto-accept'. */
    role?: string;
    /** Skip desktop onboarding flows during automation. */
    skipOnboarding: boolean;
    /** Force all media transports to use SFU instead of P2P. */
    forceSfu: boolean;
    /** Use Chromium fake media devices during automation. */
    useFakeMedia: boolean;
    /** Log PopH264 encoder data-channel messages (sli, receiver_ready) to console. */
    logEncoderDataMessages: boolean;
    /** Override Pop-owned FEC negotiation. --fec or --no-fec. */
    fec?: boolean;
    /** Override Pop-owned FEC maximum overhead percentage. */
    fecOverheadPercentMax?: number;
    /** Override Pop-owned FEC minimum overhead percentage. */
    fecOverheadPercentMin?: number;
    /** Override Pop-owned FEC loss estimator window. */
    fecLossWindowMs?: number;
    /** Override Pop-owned NACK negotiation. --nack or --no-nack. */
    nack?: boolean;
    /** Override Pop-owned anchored SLI recovery negotiation. --sli or --no-sli. */
    sli?: boolean;
    /** Override Pop-owned keyframe request negotiation. --keyframe-request or --no-keyframe-request. */
    keyframeRequest?: boolean;
    /** Override PopH264 feedback datachannel format. */
    popFeedbackFormat?: 'j' | 'cj';
    /** URL-safe or raw JSON PopH264 impairment config to negotiate through SDP. */
    impairment?: string;
    /** Override retina (2x) screen capture resolution. --retina or --no-retina. */
    retina?: boolean;
    /** Override whether the microphone auto-enables when joining a session. */
    microphoneOnStart?: boolean;
    /** Override whether the camera auto-enables when joining a session. */
    cameraOnStart?: boolean;
    /**
     * Launch with the "disable fast screen sharing" local setting forced on/off
     * (--disable-poph264). Applied before any connection so the first session's
     * screen share negotiates plain openh264 instead of PopH264.
     */
    disablePopH264?: boolean;
    /** Run the standalone WebRTC relay probe on app startup and log the verdict. */
    relayProbeOnStart?: boolean;
  };
};

export type Permission = 'microphone' | 'camera' | 'screen' | 'accessibility';
export type SystemSettingsPane = 'microphone' | 'camera' | 'screenCapture' | 'accessibility';

export type PreloadConfig = {
  shouldInitCrashReporter: boolean;
};

export type PopDevToolsRelayLevel = 'debug' | 'info' | 'warn' | 'error';

export interface PopDevToolsRelayEvent {
  ts: number;
  level: PopDevToolsRelayLevel;
  message: string;
  data?: unknown;
}

/* eslint-enable typescript-sort-keys/interface */
export interface IpcToMain {
  closeWindow(windowId: WindowId): void;
  getIsVisible(windowId: WindowId): boolean;
  setIsVisible(windowId: WindowId, isVisible: boolean): Promise<void>;
  showInactive(windowId: WindowId): void;
  setIgnoreMouseEvents(windowId: WindowId, options: { shouldIgnore: boolean }): void;
  setAlwaysOnTop(
    windowId: WindowId,
    level: Parameters<Electron.BrowserWindow['setAlwaysOnTop']>[1] | false,
    relativeLevel?: number,
  ): void;
  setUserId(userId: string | null): void;
  setVisibleOnAllWorkspaces(
    windowId: WindowId,
    options: {
      isVisible: boolean;
      options?: {
        visibleOnFullScreen?: boolean;
        skipTransformProcessType?: boolean;
      };
    },
  ): void;
  setBackgroundMaterial(
    windowId: WindowId,
    material: Electron.BrowserWindowConstructorOptions['backgroundMaterial'],
  ): void;
  setBackgroundColor(
    windowId: WindowId,
    color: Electron.BrowserWindowConstructorOptions['backgroundColor'],
  ): void;
  setTitleBarOverlay(windowId: WindowId, options: Electron.TitleBarOverlayOptions): void;
  setTitle(windowId: WindowId, title: string): void;
  setOrigin(windowId: WindowId, origin: Point): void;
  setSize(windowId: WindowId, size: Size, shouldAnimate?: boolean): void;
  setBounds(windowId: WindowId, rect: Rectangle): void;
  center(windowId: WindowId): void;
  setFastContentProtection(windowId: WindowId, toggle: boolean): void;
  setTrafficLightVisibility(windowId: WindowId, toggle: boolean): void;
  setTrafficLightPosition: (windowId: WindowId, position: Point) => void;
  setHiddenInMissionControl: (windowId: WindowId, toggle: boolean) => void;
  setResizable: (windowId: WindowId, toggle: boolean) => void;
  setMinimumSize: (windowId: WindowId, width: number, height: number) => void;
  setAspectRatio: (windowId: WindowId, aspectRatio: number, extraSize?: Size) => void;
  setFocusable: (windowId: WindowId, toggle: boolean) => void;
  setHasShadow: (windowId: WindowId, toggle: boolean) => void;
  setIsFocused: (windowId: WindowId, toggle: boolean) => void;
  /**
   * Notify the desktop app that our media is active for this window to show the user in trays/icons.
   */
  markMediaIsActive(windowId: WindowId, toggle: boolean): void;

  toggleUpdateScreensSubscription(toggle: boolean): void;
  togglePreventSleep(toggle: boolean): void;
  getAppLaunchInfo(): AppLaunchInfo;
  askPermission(name: Exclude<Permission, 'screen'>): Promise<boolean>;
  checkPermission(name: Permission): boolean;
  /**
   * Ask main to start (true) or stop (false) watching macOS accessibility
   * trust; while watching, main pushes `onSystemPermissionChanged` when the
   * value flips. Idempotent in both directions. The client drives this rather
   * than main starting it on its own, so the feature can be switched off with
   * a client-only update — the desktop shell ships on a much slower cadence.
   * No-op off macOS.
   */
  toggleAccessibilityPermissionWatcher(shouldWatch: boolean): void;
  /**
   * Park the calling auth window beside System Settings; resolves its side and
   * settled bounds for the drag demonstration, or false on failure. Older shells
   * return only the direction.
   */
  startPermissionSettingsPlacement(
    authWindowId: WindowId,
  ): Promise<'left' | 'right' | { direction: 'left' | 'right'; settingsBounds: Rectangle } | false>;
  stopPermissionSettingsPlacement(authWindowId: WindowId): void;
  getPlatformInfo(): {
    osName: 'mac' | 'windows' | 'linux';
    osVersion: string;
  };
  getCursorScreenPoint(): Point;
  /** Shows or hides the real native tray icon, returning its actual bounds when available. */
  setTrayVisible(visible: boolean): Promise<Rectangle | undefined>;
  getAppVersion(): string;
  /** Open a valid HTTP or HTTPS URL. The main process validates this privileged operation. */
  openExternalUrl(url: string): void;
  openSystemSettingsPane(pane: SystemSettingsPane): Promise<boolean>;
  getIsCurrentlyPlayingMedia(mediaPlayerTypes: MediaPlayerType[]): boolean[];
  openDevTools(windowId: WindowId): void;
  setAutoLaunch(shouldAutoLaunch: boolean): Promise<void>;
  getAutoLaunch(): Promise<boolean>;
  // POP-1215: drive Electron's app-global nativeTheme.themeSource from the renderer's userChosenTheme
  // so native window chrome (traffic lights, vibrancy) follows the chosen theme. 'auto' → 'system'.
  setThemeSource(source: 'system' | 'light' | 'dark'): void;
  downloadUpdate(feedUrl: string): Promise<{ isOk: boolean; error?: string; version?: string }>;
  quitAndInstall(expectedVersion: string, didUserRequestUpdate?: boolean): void;
  setClientLoadingWindowProgress(percent: number | null): void;
  setLoadingWindowProgress(percent: number | null): void;
  finishLoadingWindowAfterCurrentLoop(): Promise<void>;
  onReadyForQuit(): void;
  restartApp(didUserRequestUpdate: boolean): void;
  requestIagoPort(): void;
  toggleActivityDetection(toggle: boolean): void;
  setShouldBlockKeyboardEvents(shouldBlockKeyboardEvents: boolean): void;
  toggleIagoEventListener(payload: { isActive: boolean; role: 'host' | 'guest' }): void;
  resetGlobalAcceleratorListeners(): void;
  toggleGlobalAcceleratorListener(payload: { accelerator: string; toggle: boolean }): {
    registered: boolean;
    error?: string;
  };
  toggleTranscriber(toggle: boolean): void;
  transcribeAudio(buffer: Float32Array): void;
  setTranscriptionDebugCapture(enabled: boolean): void;
  revealTranscriptionDebugCaptures(): void;
  // macOS only (no-op elsewhere): 'regular' = dock icon + Cmd-Tab entry, 'accessory' =
  // menu-bar-style app (no dock icon/dot, no Cmd-Tab). Driven by primary-window
  // visibility — see desktop-windows.epics.ts (client) and dock-policy.ts (main).
  setActivationPolicy(policy: 'regular' | 'accessory'): Promise<void>;
  restoreHostControl(): void;
  reserveDisplayMediaRequestForDisplayId(payload: {
    displayId: string;
    index: number;
    // When true, the main process grants loopback (system/application) audio
    // for this capture so getDisplayMedia returns a system-audio track
    // alongside the screen video (POP-1432).
    withAudio?: boolean;
  }): void;
  disableNV12Capture(): void;
  quit(): void;
  setIsFakeHostCursorVisible(isVisible: boolean): void;
  setFakeHostCursorType(cursorType: 'arrow' | 'pencil'): void;
  setIsCursorVisible(isVisible: boolean): void;
  setIsSystemCursorVisible(isVisible: boolean): void;
  setShouldBlockMouseEvents(shouldBlock: boolean): void;
  debugForceStopIagoListener(): void;
  debugForceStopIagoCursorListener(): void;
  debugForceStopTranscriber(): void;
  // Dev-only (Pop DevTools): save an export through a native save dialog.
  // Returns the chosen path, or undefined when canceled/disabled.
  popDevToolsSaveExport(defaultFilename: string, text: string): Promise<string | undefined>;
  // Beta/prod Pop DevTools opt-in (debug page): flips the desktop-side gate for
  // this app run (tray item + main-process log relay). In-memory only — resets
  // on app restart; a reloaded core window restores from the getter.
  popDevToolsSetRuntimeOptIn(enabled: boolean): void;
  popDevToolsGetRuntimeOptIn(): boolean;
}

export interface IpcToClient {
  updateScreens: (payload: { screens: ScreenInfo[]; primaryId: string }) => void;
  onIagoEvent: (event: LocalIagoEvent) => void;
  onPowerMonitorEvent: (eventName: 'suspend' | 'resume' | 'lock-screen' | 'unlock-screen') => void;
  onActivityDetected: (activity: {
    lastMouseLocation?: Point;
    lastTypingTs?: number;
    lastMouseTs?: number;
  }) => void;
  onGlobalAccelerator: (accelerator: string) => void;
  onTranscript: (caption: { final: string; candidate: string; id: string }) => void;
  onTranscriptionDebugCaptureStopped: (reason: 'error' | 'size-cap') => void;
  onFullscreen: (windowId: WindowId, isFullscreen: boolean) => void;
  onDesktopWindowCloseRequested: (windowId: WindowId) => void;
  onProtocolUrl: (url: string) => void;
  onAppChangeKeyed: (isKey: boolean) => void;
  // Pushed by the main-process accessibility watcher (enabled by the client via
  // toggleAccessibilityPermissionWatcher) when the permission flips while the app
  // is running. The client reads the initial value itself at boot via
  // checkPermission.
  onSystemPermissionChanged: (name: Permission, isPermitted: boolean) => void;
  onShowDashboardRequested: (trayBounds?: Rectangle) => void;
  onToggleDashboardRequested: (trayBounds?: Rectangle) => void;
  onOpenPopDevToolsRequested: () => void;
  onPopDevToolsEvents: (events: PopDevToolsRelayEvent[]) => void;
  onBeforeQuit: () => void;
}
/* eslint-disable typescript-sort-keys/interface */
